/**
 * Iter 253y · Helpdesk Intelligence Dashboard.
 * Turns raw tickets into manager-facing decisions: SLA health, category
 * heatmap, priority mix, agent scoreboard, response/resolve times.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, Users, Ticket, RefreshCw, ArrowLeft, TrendingUp } from "lucide-react";

const PRI_TONE = {
  urgent: "bg-rose-100 text-rose-800 border-rose-300",
  high: "bg-amber-100 text-amber-800 border-amber-300",
  normal: "bg-slate-100 text-slate-700 border-slate-300",
  low: "bg-sky-50 text-sky-700 border-sky-300",
};

function fmtMins(m) {
  if (m === null || m === undefined) return "—";
  if (m < 60) return `${m.toFixed(0)}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

export default function HelpdeskIntelligence() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/helpdesk/intelligence`, { params: { days } });
      setData(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, days]);

  const priMax = useMemo(() => Math.max(1, ...Object.values(data?.priority_mix || { normal: 1 })), [data]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="helpdesk-intel-page">
      <Link to="/app/helpdesk" className="text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground">
        <ArrowLeft className="w-3 h-3" /> Inbox
      </Link>
      <PageHeader title="Helpdesk Intelligence"
                  subtitle="Turn raw tickets into decisions — SLA health, category heatmap, agent scoreboard."
                  icon={TrendingUp} />
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-widest text-muted-foreground">Window:</span>
        <select className="border border-border h-8 px-2 text-sm bg-background rounded-none"
                value={days} onChange={(e) => setDays(Number(e.target.value))}
                data-testid="hd-intel-window">
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
          <option value={180}>180 days</option>
        </select>
        <Button variant="outline" size="sm" onClick={load} className="rounded-none h-8" data-testid="hd-intel-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <span className="ml-auto text-[10px] text-muted-foreground">
          {data?.generated_at ? `updated ${new Date(data.generated_at).toLocaleString()}` : ""}
        </span>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="hd-intel-kpis">
            <Kpi icon={Ticket} label="Created" value={data.totals.created} tone="slate" />
            <Kpi icon={Clock} label="Open" value={data.totals.open} tone="sky" />
            <Kpi icon={AlertTriangle} label="Breached" value={data.totals.breached} tone="rose"
                  testKey="hd-intel-breached" />
            <Kpi icon={AlertTriangle} label="At risk" value={data.totals.at_risk} tone="amber" />
            <Kpi icon={Users} label="Resolved" value={data.totals.resolved} tone="emerald" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Avg first-response</div>
              <div className="text-2xl font-semibold mt-1">{fmtMins(data.durations.avg_first_response_mins)}</div>
            </div>
            <div className="border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Median resolve</div>
              <div className="text-2xl font-semibold mt-1">{fmtMins(data.durations.median_resolve_mins)}</div>
            </div>
            <div className="border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Avg resolve</div>
              <div className="text-2xl font-semibold mt-1">{fmtMins(data.durations.avg_resolve_mins)}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="border border-border" data-testid="hd-intel-by-queue">
              <div className="px-3 py-2 border-b border-border text-[11px] uppercase tracking-widest text-muted-foreground">Queue load</div>
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-3 py-1 font-semibold uppercase text-[10px] tracking-widest">Queue</th>
                    <th className="text-right px-3 py-1 font-semibold uppercase text-[10px] tracking-widest">Total</th>
                    <th className="text-right px-3 py-1 font-semibold uppercase text-[10px] tracking-widest">Open</th>
                    <th className="text-right px-3 py-1 font-semibold uppercase text-[10px] tracking-widest">Resolved</th>
                    <th className="text-right px-3 py-1 font-semibold uppercase text-[10px] tracking-widest">Breached</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_queue.length === 0 && <tr><td colSpan="5" className="p-3 text-muted-foreground italic text-center">No tickets in window</td></tr>}
                  {data.by_queue.map((q) => (
                    <tr key={q.queue_id} className="border-t border-border" data-testid={`hd-intel-queue-${q.queue_id}`}>
                      <td className="px-3 py-1">{q.queue_name}</td>
                      <td className="px-3 py-1 text-right">{q.total}</td>
                      <td className="px-3 py-1 text-right">{q.open}</td>
                      <td className="px-3 py-1 text-right text-emerald-700">{q.resolved}</td>
                      <td className={`px-3 py-1 text-right ${q.breached > 0 ? "text-rose-700 font-semibold" : "text-muted-foreground"}`}>{q.breached}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="border border-border" data-testid="hd-intel-priority-mix">
              <div className="px-3 py-2 border-b border-border text-[11px] uppercase tracking-widest text-muted-foreground">Priority mix</div>
              <div className="p-3 space-y-2">
                {Object.entries(data.priority_mix).map(([p, ct]) => (
                  <div key={p} className="flex items-center gap-2" data-testid={`hd-intel-priority-${p}`}>
                    <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 border ${PRI_TONE[p]}`} style={{ minWidth: 66, textAlign: "center" }}>{p}</span>
                    <div className="flex-1 h-3 bg-muted">
                      <div className="h-3" style={{ width: `${(ct / priMax) * 100}%`, background: p === "urgent" ? "#dc2626" : p === "high" ? "#eab308" : p === "normal" ? "#64748b" : "#0ea5e9" }} />
                    </div>
                    <span className="text-xs font-mono w-8 text-right">{ct}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="border border-border" data-testid="hd-intel-by-category">
            <div className="px-3 py-2 border-b border-border text-[11px] uppercase tracking-widest text-muted-foreground">Category heatmap</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-3 py-1 font-semibold uppercase text-[10px] tracking-widest">Category</th>
                    <th className="text-right px-3 py-1 font-semibold uppercase text-[10px] tracking-widest">Volume</th>
                    <th className="text-right px-3 py-1 font-semibold uppercase text-[10px] tracking-widest">Avg resolve (h)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_category.length === 0 && <tr><td colSpan="3" className="p-3 text-muted-foreground italic text-center">No tickets in window</td></tr>}
                  {data.by_category.map((c) => {
                    const heat = c.avg_resolve_hours || 0;
                    const bg = heat > 24 ? "bg-rose-50" : heat > 8 ? "bg-amber-50" : heat > 0 ? "bg-emerald-50" : "";
                    return (
                      <tr key={c.category_id} className={`border-t border-border ${bg}`} data-testid={`hd-intel-cat-${c.category_id}`}>
                        <td className="px-3 py-1">{c.category_name}</td>
                        <td className="px-3 py-1 text-right">{c.volume}</td>
                        <td className="px-3 py-1 text-right">{c.avg_resolve_hours ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border border-border" data-testid="hd-intel-scoreboard">
            <div className="px-3 py-2 border-b border-border text-[11px] uppercase tracking-widest text-muted-foreground">Agent scoreboard</div>
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-3 py-1 font-semibold uppercase text-[10px] tracking-widest">Agent</th>
                  <th className="text-right px-3 py-1 font-semibold uppercase text-[10px] tracking-widest">Assigned</th>
                  <th className="text-right px-3 py-1 font-semibold uppercase text-[10px] tracking-widest">Resolved</th>
                  <th className="text-right px-3 py-1 font-semibold uppercase text-[10px] tracking-widest">Avg resolve (h)</th>
                </tr>
              </thead>
              <tbody>
                {data.scoreboard.length === 0 && <tr><td colSpan="4" className="p-3 text-muted-foreground italic text-center">No assignments in window</td></tr>}
                {data.scoreboard.map((a) => (
                  <tr key={a.agent_id} className="border-t border-border" data-testid={`hd-intel-agent-${a.agent_id}`}>
                    <td className="px-3 py-1">{a.agent_name}</td>
                    <td className="px-3 py-1 text-right">{a.assigned}</td>
                    <td className="px-3 py-1 text-right text-emerald-700">{a.resolved}</td>
                    <td className="px-3 py-1 text-right">{a.avg_resolve_hours ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone = "slate", testKey }) {
  const toneCls = {
    slate: "text-slate-700", sky: "text-sky-700", rose: "text-rose-700",
    amber: "text-amber-700", emerald: "text-emerald-700",
  }[tone] || "text-slate-700";
  return (
    <div className="border border-border p-3 flex items-center gap-3" data-testid={testKey}>
      <Icon className={`w-5 h-5 ${toneCls}`} />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`text-2xl font-semibold ${toneCls}`}>{value}</div>
      </div>
    </div>
  );
}
